import { useSelector } from "react-redux";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { RootState } from "../../../../../../../redux/store";
import ChartCard, { ChartEmptyState } from "./ChartCard";
import VerifiedIcon from "../../../../../../../assets/svg/VerifiedIcon";
import AnalyticsIcon from "../../../../../../../assets/svg/analytics-icon";
import { StageChartItem } from "../utils/constants";
import { useMemo } from "react";
import { getHelpdeskLabels } from "../../../../../../../utils/constants/mappings";

type TickProps = {
  x?: number;
  y?: number;
  payload?: { value: string };
  colorMap?: Record<string, string>;
  textColorMap?: Record<string, string>;
  yAxisWidth?: number;
};
type LabelProps = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  value?: number;
};

const StatusTick = ({
  x = 0,
  y = 0,
  payload,
  colorMap = {},
  textColorMap = {},
  yAxisWidth = 100,
}: TickProps) => {
  const status = payload?.value ?? "";
  const bgColor = colorMap[status] ?? "#252b37";
  const textColor = textColorMap[status] ?? "#ffffff";
  return (
    <g transform={`translate(${x},${y})`}>
      <foreignObject x={-yAxisWidth} y={-11} width={yAxisWidth} height={22}>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            height: "100%",
          }}
        >
          <span
            style={{
              backgroundColor: bgColor,
              color: textColor,
              fontWeight: 600,
              padding: "2px 8px",
              borderRadius: "6px",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              lineHeight: 1.5,
            }}
            className="text-sm flex items-center gap-1"
          >
            {status === "CLOSED" && <VerifiedIcon className="w-2.5 h-2.5" />}
            {status}
          </span>
        </div>
      </foreignObject>
    </g>
  );
};

const ValueLabel = ({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  value,
}: LabelProps) => {
  if (!value) return null;
  return (
    <text
      x={x + width + 6}
      y={y + height / 2}
      fill="#a3a3a3"
      fontSize={12}
      fontWeight={500}
      dominantBaseline="middle"
    >
      {value}
    </text>
  );
};

interface TicketsByStatusChartProps {
  chartData: StageChartItem[];
}

const TicketsByStatusChart = ({ chartData }: TicketsByStatusChartProps) => {
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const helpdeskLabels = getHelpdeskLabels(region ?? "UK");
  const isDarkMode = useSelector(
    (state: RootState) => state?.themeProvider?.isDarkMode
  );
  const gridStroke = isDarkMode ? "#393f4a" : "#e9eaeb";
  const barFill = isDarkMode ? "#fbfbfb" : "#252b37";

  const colorMap = useMemo(
    () => Object.fromEntries(chartData?.map((s) => [s?.status, s?.color])),
    [chartData]
  );

  const textColorMap = useMemo(
    () =>
      Object.fromEntries(
        chartData
          ?.filter((s) => s?.textColor)
          ?.map((s) => [s?.status, s?.textColor as string])
      ),
    [chartData]
  );

  const maxValue = useMemo(() => {
    if (!chartData?.length) return 100;
    const max = Math.max(...chartData.map((s) => s.value));
    const step = max <= 10 ? 2 : max <= 50 ? 10 : max <= 200 ? 50 : 100;
    return Math.ceil(max / step) * step || step;
  }, [chartData]);

  const xTicks = useMemo(() => {
    const step = maxValue / 5;
    return Array.from({ length: 6 }, (_, i) => Math.round(i * step));
  }, [maxValue]);

  const yAxisWidth = useMemo(() => {
    if (!chartData?.length) return 80;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    let maxPillWidth = 0;
    for (const s of chartData) {
      const text = (s.status ?? "").toUpperCase();
      let textWidth: number;
      if (ctx) {
        ctx.font = "600 12px sans-serif";
        textWidth = ctx.measureText(text).width;
      } else {
        textWidth = text.length * 7;
      }
      const hasIcon = s.status === "CLOSED";
      const pillWidth = textWidth + 16 + (hasIcon ? 14 : 0);
      maxPillWidth = Math.max(maxPillWidth, pillWidth);
    }
    return Math.ceil(maxPillWidth) + 4;
  }, [chartData]);

  return (
    <ChartCard
      title={`${helpdeskLabels.ticketPlural} by Status`}
      description={`Distribution of ${helpdeskLabels.ticketPluralLower} across all status`}
      headerIcon={
        <AnalyticsIcon className="w-4.5 h-4.5 text-verba-indigo dark:text-verba-indigo-dk" />
      }
      className="h-full"
    >
      {chartData?.length === 0 ? (
        <ChartEmptyState />
      ) : (
        <div
          className="mt-auto pb-4"
          style={{ height: chartData.length * 36 + 24 }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 4, right: 30, bottom: 0, left: 0 }}
              barSize={8}
            >
              <CartesianGrid
                horizontal={false}
                vertical={false}
                stroke={gridStroke}
                strokeDasharray="3 3"
              />
              <XAxis
                type="number"
                domain={[0, maxValue]}
                ticks={xTicks}
                tickLine={false}
                axisLine={{ stroke: gridStroke }}
                tick={{
                  fill: isDarkMode ? "#fbfbfb" : "#252b37",
                  fontSize: 10,
                }}
              />
              <YAxis
                type="category"
                dataKey="status"
                width={yAxisWidth}
                tickLine={false}
                axisLine={false}
                tick={(props: TickProps) => (
                  <StatusTick
                    {...props}
                    colorMap={colorMap}
                    textColorMap={textColorMap}
                    yAxisWidth={yAxisWidth}
                  />
                )}
                interval={0}
              />
              <Bar
                dataKey="value"
                fill={barFill}
                radius={[4, 4, 4, 4]}
                label={(props: LabelProps) => <ValueLabel {...props} />}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </ChartCard>
  );
};

export default TicketsByStatusChart;
